/**
 * Schemas Zod do backbone procurement / leilão reverso.
 *
 * Origem: docs/codex-reverse-auction-prep/07-procurement-backbone-schemas.md (D7).
 * Implementação 100% aditiva · paralela a quotation_rounds (Wave 96 / RFQ legado).
 *
 * Modalidades suportadas:
 *   direct          — fornecedor pré-escolhido (compra rápida)
 *   rfq             — RFQ tradicional (1 proposta por fornecedor, PM compara)
 *   reverse_auction — leilão reverso (lances iterativos)
 *   framework       — drawdown contra contrato guarda-chuva
 *
 * Wave 1 entrega: schemas + tabelas + services puros (placeBid, awardRound).
 * Wave 1.5 entrega: framework_contracts + drawdown.
 * Wave 2 entrega: recommendModality + WhatsApp + Cockpit.
 */
import { z } from 'zod';

// ─── Enums base ────────────────────────────────────────────────────────

export const procurementModalitySchema = z.enum([
  'direct',
  'rfq',
  'reverse_auction',
  'framework',
]);
export type ProcurementModality = z.infer<typeof procurementModalitySchema>;

export const procurementIntentStatusSchema = z.enum([
  'draft',
  'ready',
  'running',
  'awarded',
  'ordered',
  'cancelled',
]);
export type ProcurementIntentStatus = z.infer<typeof procurementIntentStatusSchema>;

export const procurementRoundStatusSchema = z.enum([
  'draft',
  'scheduled',
  'running',
  'ended',
  'cancelled',
  'failed',
]);
export type ProcurementRoundStatus = z.infer<typeof procurementRoundStatusSchema>;

/**
 * Freight terms · explícito no leilão · 2026-05-11
 * Evita surpresa de "preço menor ganha mas + 3x frete" quebrando o saving.
 *  - included: CIF, preço já inclui frete (padrão BR construção)
 *  - separate: FOB, fornecedor cota frete num campo separado · ranking total
 *  - pickup:   Ex-Works, obra retira (sem frete por conta do fornecedor)
 */
export const freightTermsSchema = z.enum(['included', 'separate', 'pickup']);
export type FreightTerms = z.infer<typeof freightTermsSchema>;

// ─── Itens da intent ───────────────────────────────────────────────────

export const procurementItemSchema = z.object({
  id: z.string().min(1),
  intentId: z.string().min(1),
  skuId: z.string().min(1).nullable().optional(),
  budgetItemId: z.string().min(1).nullable().optional(),
  description: z.string().trim().min(1).max(300),
  quantity: z.number().positive(),
  unit: z.string().trim().min(1).max(30),
  deliveryAddress: z.string().trim().min(1).max(500),
  deliveryDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve ser YYYY-MM-DD')
    .nullable()
    .optional(),
  ceilingPriceCents: z.number().int().nonnegative().nullable().optional(),
  targetPriceCents: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type ProcurementItem = z.infer<typeof procurementItemSchema>;

// ─── Intent ───────────────────────────────────────────────────────────

export const procurementIntentSchema = z.object({
  id: z.string().min(1),
  projectId: z.string().min(1),
  title: z.string().trim().min(2).max(180),
  items: z.array(procurementItemSchema).min(1).max(200),
  modality: procurementModalitySchema,
  /** Default 'included' (BR · CIF) · evita "+ 3x de frete surpresa". */
  freightTerms: freightTermsSchema.default('included'),
  status: procurementIntentStatusSchema,
  createdBy: z.string().min(1),
  createdAt: z.string().datetime({ offset: true }),
});
export type ProcurementIntent = z.infer<typeof procurementIntentSchema>;

// ─── Configurações por modalidade ──────────────────────────────────────

const auctionConfigFields = {
  /** Teto absoluto (preço inicial · ninguém lança acima). */
  ceilingCents: z.number().int().positive(),
  /** Preço-alvo (PM espera; opcional). */
  targetCents: z.number().int().positive().nullable().optional(),
  /** Decremento mínimo absoluto (ex: R$ 0,50 = 50). */
  decrementMinCents: z.number().int().nonnegative().nullable().optional(),
  /** OU decremento mínimo percentual (ex: 1 = 1%). */
  decrementMinPct: z.number().min(0).max(100).nullable().optional(),
  /** Duração padrão (express/standard/extended ou custom). */
  durationKind: z.enum(['express', 'standard', 'extended', 'custom']),
  /** Duração em minutos (5min - 7 dias). */
  durationMinutes: z.number().int().min(5).max(7 * 24 * 60),
  /** Anti-sniping: se lance no último N minutos, estende N min. 0 desabilita. */
  antiSnipingMinutes: z.number().int().min(0).max(60),
  /** Visibilidade pros fornecedores durante o leilão. */
  visibility: z.enum(['own_only', 'best_only', 'ranking_anonymous']),
  /** Mínimo de participantes pra round adjudicar. */
  minParticipants: z.number().int().min(1).max(50),
  /** Exige confirmação final do vencedor antes de virar PO. */
  requireFinalConfirm: z.boolean(),
  /** Exige termo de uso aceito antes de qualquer lance. */
  requireAcceptedTerms: z.boolean(),
} satisfies z.ZodRawShape;

const auctionConfigObjectSchema = z.object(auctionConfigFields);

function validateAuctionConfig(
  v: z.infer<typeof auctionConfigObjectSchema>,
  ctx: z.RefinementCtx,
): void {
  if (v.decrementMinCents == null && v.decrementMinPct == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Informe decrementMinCents ou decrementMinPct.',
      path: ['decrementMinCents'],
    });
  }
  if (v.targetCents != null && v.targetCents > v.ceilingCents) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'targetCents deve ser menor ou igual ao teto.',
      path: ['targetCents'],
    });
  }
}

export const auctionConfigSchema = auctionConfigObjectSchema.superRefine(
  validateAuctionConfig,
);
export type AuctionConfig = z.infer<typeof auctionConfigSchema>;

export const directConfigSchema = z.object({
  modality: z.literal('direct'),
  preferredSupplierId: z.string().min(1).nullable().optional(),
  reason: z.string().trim().max(1000).nullable().optional(),
});

export const rfqConfigSchema = z.object({
  modality: z.literal('rfq'),
  minQuotes: z.number().int().min(1).max(20).default(3),
  deadlineAt: z.string().datetime({ offset: true }).nullable().optional(),
  inviteSupplierIds: z.array(z.string().min(1)).max(50).default([]),
  allowPerLineAward: z.boolean().default(true),
});

export const reverseAuctionConfigSchema = z.object({
  modality: z.literal('reverse_auction'),
  ...auctionConfigFields,
});

export const frameworkConfigSchema = z.object({
  modality: z.literal('framework'),
  frameworkContractId: z.string().min(1),
  drawdownLimitCents: z.number().int().positive().nullable().optional(),
  priceTableVersion: z.string().trim().max(60).nullable().optional(),
});

const modalityConfigObjectSchema = z.discriminatedUnion('modality', [
  directConfigSchema,
  rfqConfigSchema,
  reverseAuctionConfigSchema,
  frameworkConfigSchema,
]);

export const modalityConfigSchema = modalityConfigObjectSchema.superRefine(
  (v, ctx) => {
    if (v.modality === 'reverse_auction') validateAuctionConfig(v, ctx);
  },
);
export type ModalityConfig = z.infer<typeof modalityConfigSchema>;

// ─── Round ────────────────────────────────────────────────────────────

export const procurementRoundSchema = z.object({
  id: z.string().min(1),
  intentId: z.string().min(1),
  modality: procurementModalitySchema,
  configuration: modalityConfigSchema,
  startedAt: z.string().datetime({ offset: true }).nullable(),
  endedAt: z.string().datetime({ offset: true }).nullable(),
  status: procurementRoundStatusSchema,
});
export type ProcurementRound = z.infer<typeof procurementRoundSchema>;

// ─── Bid (lance atômico) ──────────────────────────────────────────────

export const bidSchema = z.object({
  id: z.string().min(1),
  roundId: z.string().min(1),
  supplierId: z.string().min(1),
  itemId: z.string().min(1),
  priceCents: z.number().int().nonnegative(),
  /** Frete · só usado quando intent.freightTerms='separate'. */
  freightCents: z.number().int().nonnegative().nullable().optional(),
  deliveryDays: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
  ipHash: z.string().min(16).max(128),
  userAgentHash: z.string().min(16).max(128),
  placedAt: z.string().datetime({ offset: true }),
});
export type Bid = z.infer<typeof bidSchema>;

// ─── Award ────────────────────────────────────────────────────────────

export const procurementAwardItemSchema = z.object({
  itemId: z.string().min(1),
  quantity: z.number().positive(),
  unitPriceCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  bidId: z.string().min(1).nullable().optional(),
});
export type ProcurementAwardItem = z.infer<typeof procurementAwardItemSchema>;

export const procurementAwardSchema = z.object({
  id: z.string().min(1),
  intentId: z.string().min(1),
  roundId: z.string().min(1),
  supplierId: z.string().min(1),
  items: z.array(procurementAwardItemSchema).min(1).max(200),
  totalCents: z.number().int().nonnegative(),
  paymentTerms: z.string().trim().max(1000).nullable().optional(),
  status: z.enum(['proposed', 'confirmed', 'rejected', 'cancelled']),
  awardedBy: z.string().min(1),
  awardedAt: z.string().datetime({ offset: true }),
});
export type ProcurementAward = z.infer<typeof procurementAwardSchema>;

// ─── Request shapes (input público) ────────────────────────────────────

export const createProcurementIntentRequestSchema = z.object({
  projectId: z.string().min(1),
  title: z.string().trim().min(2).max(180),
  modality: procurementModalitySchema,
  /** Default 'included' · obrigatório o PM escolher pra evitar surpresa de frete. */
  freightTerms: freightTermsSchema.default('included'),
  items: z
    .array(
      procurementItemSchema.omit({ id: true, intentId: true }),
    )
    .min(1)
    .max(200),
});
export type CreateProcurementIntentRequest = z.infer<
  typeof createProcurementIntentRequestSchema
>;

export const placeBidRequestSchema = z.object({
  itemId: z.string().min(1),
  priceCents: z.number().int().nonnegative(),
  /** Só requerido quando intent.freightTerms='separate'. Service valida. */
  freightCents: z.number().int().nonnegative().nullable().optional(),
  deliveryDays: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
});
export type PlaceBidRequest = z.infer<typeof placeBidRequestSchema>;

// ─── Modality Recommender (LR-W2 · #50 · shadow only) ──────────────────
//
// Decision tree fonte: docs/codex-reverse-auction-prep/10-operational-cost-pm-time.md
// "Decision tree". Roda em shadow mode (não decide nada · PM ainda escolhe).
// Toda chamada vira uma row em `model_inference_log` (migration 0170).

/**
 * Reason codes determinísticos (1:1 com branches do decision tree).
 * Frontend pode mapear pra mensagens curtas no Cockpit PM (#51).
 */
export const modalityRecommenderReasonCodeSchema = z.enum([
  'urgent_small_po',
  'recurring_with_framework_table',
  'recurring_high_volume_multi_supplier',
  'po_below_threshold',
  'low_sku_confidence',
  'low_supplier_count',
  'technical_item_high_value',
  'technical_item_low_value',
  'commodity_high_value',
  'mid_value_default_rfq',
  'fallback_default',
]);
export type ModalityRecommenderReasonCode = z.infer<
  typeof modalityRecommenderReasonCodeSchema
>;

/**
 * Snapshot do item sendo cotado. Tudo opcional onde o pseudocode tolera
 * default; obrigatório onde a regra precisa de número (PO base).
 */
export const modalityRecommenderItemInputSchema = z.object({
  /** PO total estimado em centavos. Se null, fallback pra ceilingPriceCents. */
  estimatedTotalCents: z.number().int().nonnegative().nullable().optional(),
  /** Teto de preço (se PM informou). Usado quando estimatedTotalCents null. */
  ceilingPriceCents: z.number().int().nonnegative().nullable().optional(),
  /** 0-5 · commodity score do catálogo (5 = totalmente fungível). */
  commodityScore: z.number().min(0).max(5).nullable().optional(),
  /** 0-1 · confiança do match SKU canônico. <0.85 força RFQ. */
  skuConfidence: z.number().min(0).max(1).nullable().optional(),
  /** YYYY-MM-DD · usado pra detectar urgência (entrega ≤ 2d). */
  deliveryDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'deliveryDate deve ser YYYY-MM-DD')
    .nullable()
    .optional(),
  requiresInstall: z.boolean().nullable().optional(),
  requiresEngineeringSpec: z.boolean().nullable().optional(),
});
export type ModalityRecommenderItemInput = z.infer<
  typeof modalityRecommenderItemInputSchema
>;

export const modalityRecommenderHistoryInputSchema = z.object({
  /** Quantos POs do mesmo SKU nos últimos 180d. */
  sameSkuOrdersLast180d: z.number().int().nonnegative(),
  /** Volume mensal médio do SKU em centavos. */
  monthlyVolumeCents: z.number().int().nonnegative(),
  /** Já existe tabela de preço framework negociada? */
  hasFrameworkPriceTable: z.boolean(),
});
export type ModalityRecommenderHistoryInput = z.infer<
  typeof modalityRecommenderHistoryInputSchema
>;

export const modalityRecommenderSuppliersInputSchema = z.object({
  /** Quantos fornecedores aprovados ativos pra SKU+região. */
  activeForSkuRegionCount: z.number().int().nonnegative(),
});
export type ModalityRecommenderSuppliersInput = z.infer<
  typeof modalityRecommenderSuppliersInputSchema
>;

export const modalityRecommenderInputSchema = z.object({
  item: modalityRecommenderItemInputSchema,
  history: modalityRecommenderHistoryInputSchema,
  suppliers: modalityRecommenderSuppliersInputSchema,
});
export type ModalityRecommenderInput = z.infer<
  typeof modalityRecommenderInputSchema
>;

export const modalityRecommenderSignalsSchema = z.object({
  poCents: z.number().int().nonnegative(),
  commodityScore: z.number().min(0).max(5),
  supplierCount: z.number().int().nonnegative(),
  skuConfidence: z.number().min(0).max(1),
  recurring: z.boolean(),
  urgent: z.boolean(),
  technical: z.boolean(),
  daysUntilDelivery: z.number().int().nullable(),
});
export type ModalityRecommenderSignals = z.infer<
  typeof modalityRecommenderSignalsSchema
>;

export const modalityRecommenderOutputSchema = z.object({
  modality: procurementModalitySchema,
  reasonCode: modalityRecommenderReasonCodeSchema,
  reason: z.string().min(1),
  signals: modalityRecommenderSignalsSchema,
  version: z.string().min(1),
});
export type ModalityRecommenderOutput = z.infer<
  typeof modalityRecommenderOutputSchema
>;
